import React, { useState, useMemo } from "react";
import { getDisplayDate } from "../../../../shared/helpers";
import { LinkAnchor } from "../../../../shared/components/form/FormElements";
import { useTranslation } from "react-i18next";
import { Card, CardBody } from "../../../../shared/components/Card";
import {
  colorRedNew,
  colorStatusActive,
} from "../../../../utils/palette";
import {
  StatusContainer,
  Table,
  RedButton,
} from "../../../../shared/components/TableElements";
import { TableWrap } from "../../../../shared/components/table/components/ReactTableConstructor";
import { ReactComponent as SortUpAngleIcon } from "../../../../shared/img/angle-small-up.svg";
import { ReactComponent as SortDownAngleIcon } from "../../../../shared/img/angle-small-down.svg";
import { NATEXT } from "../../../../shared/constants";
import SimplePagination from "./SimplePagination";
import { ColumnHeaderContainer } from "./TableStyles";

const InvoicesTable = ({ invoices }) => {
  const { t } = useTranslation("common");

  // State for sorting, pagination, and search
  const [sortConfig, setSortConfig] = useState({
    key: "created",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  // const [searchTerm, setSearchTerm] = useState("");

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    // let filtered = invoices.filter((invoice) => {
    //   if (!searchTerm) return true;
    //   const searchLower = searchTerm.toLowerCase();
    //   return (
    //     getDisplayDate(invoice.created).toLowerCase().includes(searchLower) ||
    //     invoice.amount?.toString().toLowerCase().includes(searchLower) ||
    //     invoice.invoice_status?.toLowerCase().includes(searchLower)
    //   );
    // });
    let filtered = [...invoices]; // Create a copy to avoid mutating original array

    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "created") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortConfig.key === "amount") {
        // Extract numeric value from string format like "$150.00 USD"
        aValue = parseFloat(aValue.replace(/[^0-9.-]/g, '')) || 0;
        bValue = parseFloat(bValue.replace(/[^0-9.-]/g, '')) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [invoices, sortConfig]); // Removed searchTerm dependency

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Handle search (commented out for now)
  // const handleSearch = (value) => {
  //   setSearchTerm(value);
  //   setCurrentPage(0); // Reset to first page when searching
  // };

  // Pagination handlers
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const canPreviousPage = currentPage > 0;
  const canNextPage = currentPage < totalPages - 1;

  const gotoPage = (page) => setCurrentPage(page);
  const previousPage = () => setCurrentPage((prev) => Math.max(0, prev - 1));
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));

  // Render status badge
  const renderStatus = (invoice_status) => {
    let statusData = {
      text: t("paid"),
      background: colorStatusActive,
      icon: "fi fi-rs-check-circle",
      color: "black",
    };
    if (invoice_status === "open") {
      statusData = {
        text: t("overdue"),
        background: colorRedNew,
        icon: "fi fi-rr-triangle-warning",
        color: "white",
      };
    }
    return (
      <StatusContainer background={statusData.background}>
        <i
          className={statusData.icon}
          style={{
            color: statusData.color,
            position: "relative",
            top: "2px",
            marginRight: "4px",
          }}
        />
        <p style={{ color: statusData.color }}>{statusData.text}</p>
      </StatusContainer>
    );
  };

  // Get sort icon for header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <SortUpAngleIcon fill="white" />;
    }
    return sortConfig.direction === "asc" ? (
      <SortUpAngleIcon />
    ) : (
      <SortDownAngleIcon />
    );
  };

  return (
    <>
      <Card shadow={"true"}>
        <CardBody className="p-0">
          {/* Search functionality commented out for now
          <div style={{ background: "#f9f9f9" }} className="p-3">
            <SearchInput
              id="search"
              type="search"
              placeholder={t("filter_by_keyword")}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          */}
          <TableWrap className="p-3" pagination={true}>
            <Table bordered id="invoices-data-table">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  margin: 0,
                  padding: 0,
                }}
              >
                <tr className="react-table thead tr">
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      borderBottom: "none",
                      fontWeight: 500,
                    }}
                    onClick={() => handleSort("created")}
                  >
                    <ColumnHeaderContainer>
                      <span className="react-table__column-header">
                        {t("date_tv")}
                      </span>
                      <span className="react-table__column-header sortable">
                        {getSortIcon("created")}
                      </span>
                    </ColumnHeaderContainer>
                  </th>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      borderBottom: "none",
                      fontWeight: 500,
                    }}
                    onClick={() => handleSort("amount")}
                  >
                    <ColumnHeaderContainer>
                      <span className="react-table__column-header">
                        {t("amount")}
                      </span>
                      <span className="react-table__column-header sortable">
                        {getSortIcon("amount")}
                      </span>
                    </ColumnHeaderContainer>
                  </th>
                  <th style={{ borderBottom: "none", fontWeight: 500 }}>
                    {t("status")}
                  </th>
                  <th style={{ borderBottom: "none", fontWeight: 500 }}>
                    {t("pdf")}
                  </th>
                  <th style={{ borderBottom: "none", fontWeight: 500 }}>
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((invoice, index) => (
                  <tr
                    key={invoice.id || index}
                    style={{ fontWeight: 400, color: "black" }}
                  >
                    <td style={{ fontWeight: 400, color: "black" }}>
                      {getDisplayDate(invoice.created)}
                    </td>
                    <td style={{ fontWeight: 400, color: "black" }}>
                      {invoice.amount}
                    </td>
                    <td style={{ fontWeight: 400, color: "black" }}>
                      {renderStatus(invoice.invoice_status)}
                    </td>
                    <td style={{ fontWeight: 400, color: "black" }}>
                      <LinkAnchor href={invoice.pdf}>
                        {t("download")}
                      </LinkAnchor>
                    </td>
                    <td style={{ fontWeight: 400, color: "black" }}>
                      {invoice.invoice_status === "open" ? (
                        <RedButton
                          type="button"
                          style={{
                            height: "24px",
                            width: "min-content",
                            minWidth: "min-content",
                          }}
                          onClick={() => {
                            window.open(invoice.hosted_invoice_url, "_blank");
                          }}
                        >
                          <p>{t("pay_now")}</p>
                        </RedButton>
                      ) : (
                        NATEXT
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredAndSortedData.length === 0 && (
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                {t("no_records")}
              </div>
            )}
          </TableWrap>

          {filteredAndSortedData.length > 0 && (
            <SimplePagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredAndSortedData.length}
              onPageChange={gotoPage}
              onPreviousPage={previousPage}
              onNextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setCurrentPage(0); // Reset to first page when page size changes
              }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default InvoicesTable;